import {
  IQuickVideoForm,
  IQuickVideoPartOption,
  IQuickVideoPartPositionOption,
  IVideoDetails,
  QuickVideoPartType,
} from 'lib/api/quickVideo/types';
import {
  FormikDropdownField,
  FormikDropdownOption,
} from 'lib/components/formik/FormikDropdown';
import { Gap } from 'lib/components/styles/layout';
import React, { useState } from 'react';
import { Button } from 'react-covideo-common';
import { HiPlus } from 'react-icons/hi';
import { useFormikContext } from 'formik';
import { AddVideoModal } from '../modals/AddVideoModal';
import { SelectedVideoCardSmall } from './SelectedVideoCardSmall';
import { FormikTextAreaField } from 'lib/components/formik/FormikTextAreaField';
import { useTheme } from 'styled-components/macro';
import { useTranslation } from 'react-i18next';

type VideoPositionDropdownProps = {
  quickVideoPart: IQuickVideoPartPositionOption;
  options: IQuickVideoPartOption[];
  index: number;
  replace: <X = any>(index: number, value: X) => void;
};

export const VideoPositionComponent = ({
  quickVideoPart,
  options,
  index,
  replace,
}: VideoPositionDropdownProps) => {
  const [openAddVideoModal, setOpenAddVideoModal] = useState(false);
  const { values } = useFormikContext<IQuickVideoForm>();
  const theme = useTheme();

  const { t } = useTranslation();
  const chosenFieldValues = values.quickVideoParts[index];
  const isDealershipVideo =
    chosenFieldValues?.partType === QuickVideoPartType.DEALERSHIP_VIDEO;
  const isUserVideo =
    chosenFieldValues?.partType === QuickVideoPartType.USER_VIDEO;

  const isVideoTypeSelected = !!chosenFieldValues?.partType;
  const isDealershipVideoSelected =
    isDealershipVideo && !!chosenFieldValues?.video;
  const isUserVideoSelected = isUserVideo && !!chosenFieldValues?.video;

  const translatedOptions = options.map(option => ({
    ...option,
    label: t(option.label),
  }));

  const handleQuickVideoPartChange = (
    option: FormikDropdownOption | null | undefined
  ) => {
    replace(index, {
      ...chosenFieldValues,
      partPosition: quickVideoPart.value,
      partType: option ? option.value : '',
      description: option ? chosenFieldValues.description : '', // reset description if option is cleared
      video: null,
    });
  };

  const onOpenAddVideoModal = () => {
    setOpenAddVideoModal(true);
  };

  const onCloseAddVideoModal = () => {
    setOpenAddVideoModal(false);
  };

  return (
    <>
      <Gap
        flexDirection='column'
        width='100%'
        gap='18px'
        key={quickVideoPart.value}
      >
        <FormikDropdownField
          name={`quickVideoParts[${index}].partType`}
          options={translatedOptions}
          label={t('quickVideoPartTypeLabel', {
            label: t(quickVideoPart.label),
          })}
          hideError
          isRequired={!!quickVideoPart.required}
          isClearable
          placeholder={t('quickVideoPartTypePlaceholder', {
            label: t(quickVideoPart.label).toLocaleLowerCase(),
          })}
          onChange={handleQuickVideoPartChange}
          extendStyles={{
            labelStyles: {
              color: theme.colors.neutral[80],
            },
          }}
        />

        <FormikTextAreaField
          name={`quickVideoParts[${index}].description`}
          label={t('quickVideoPartDescriptionLabel', {
            label: t(quickVideoPart.label),
          })}
          isRequired={false}
          disabled={!isVideoTypeSelected}
          maxLength={150}
          showCounter
          extendStyles={{
            labelStyles: {
              color: theme.colors.neutral[80],
            },
            textAreaStyles: {
              resize: 'none',
              height: '62px',
              minHeight: 'unset',
            },
          }}
        />
        {isDealershipVideoSelected || isUserVideoSelected ? (
          <SelectedVideoCardSmall
            video={chosenFieldValues?.video || ({} as IVideoDetails)}
            onClear={() => {
              replace(index, {
                ...chosenFieldValues,
                video: null,
              });
            }}
          />
        ) : (
          <Button
            variant='secondary'
            onClick={onOpenAddVideoModal}
            disabled={
              (!isDealershipVideo && !isUserVideo) || !isVideoTypeSelected
            }
            text={t('Add Video')}
            icon={<HiPlus />}
            style={{
              width: '100%',
              height: '77px',
              maxHeight: 'unset',
            }}
          />
        )}
      </Gap>
      {openAddVideoModal && (
        <AddVideoModal
          type={chosenFieldValues?.partType}
          onClose={onCloseAddVideoModal}
          onAddVideo={(video: IVideoDetails) => {
            replace(index, {
              ...chosenFieldValues,
              video,
            });
          }}
        />
      )}
    </>
  );
};
